export const imageFileTypes = ["jpg", "jpeg", "png", "gif"];
export const videoFileTypes = ["mp4", "mov", "ogg", "webm"];

export const roboticsFileTypes = [
  "bag",
  "csv",
  "db3",
  "log",
  "mcap",
  "ulg",
  "json",
  "parquet",
];
export const customRoboticsFileTypes = ["daq", "daq.gz", "protoLog", "vlg"];

export const plaintextFileTypes = [
  "asc",
  "bmd",
  "cfg",
  "conf",
  "csv",
  "env",
  "ini",
  "json",
  "log",
  "md",
  "rtf",
  "sql",
  "tsv",
  "txt",
  "xml",
  "yaml",
  "yml",
];

export const isImage = (filename: string) => {
  const imageFileExtensionTypes = new RegExp(
    `\\.(${imageFileTypes.join("|")})$`,
    "i",
  );
  return imageFileExtensionTypes.test(filename.toLowerCase());
};

export const isVideo = (filename: string) => {
  const videoFileExtensionTypes = new RegExp(
    `\\.(${videoFileTypes.join("|")})$`,
    "i",
  );
  return videoFileExtensionTypes.test(filename.toLowerCase());
};

export const isText = (filename: string) => {
  const plaintextFileExtensionTypes = new RegExp(
    `\\.(${plaintextFileTypes.join("|")})$`,
    "i",
  );
  return plaintextFileExtensionTypes.test(filename.toLowerCase());
};

export const isMarkdown = (filename: string): boolean => {
  return /\.(md)(\?.*)?$/i.test(filename.toLowerCase());
};

export const isHtml = (filename: string) => {
  return /\.html?(\?.*)?$/i.test(filename);
};

export const isPdf = (filename: string) => {
  return /\.pdf(\?.*)?$/i.test(filename);
};

export const isWebRenderable = (filename: string) => {
  return isHtml(filename) || isPdf(filename);
};

/**
 * Single source of truth mapping file extensions to Monaco Editor language
 * identifiers. Both {@link structuredTextFileTypes} and
 * {@link getMonacoLanguage} are derived from this map, so adding a new
 * extension automatically keeps them in sync.
 */
const extensionToMonacoLanguage: Record<string, string> = {
  json: "json",
  yaml: "yaml",
  yml: "yaml",
  xml: "xml",
};

/** Config and data files that can be viewed with syntax highlighting (JSON, YAML, XML). */
export const structuredTextFileTypes = Object.keys(extensionToMonacoLanguage);

/** Map a filename to a Monaco Editor language identifier for syntax highlighting. */
export function getMonacoLanguage(filename: string): string {
  const lower = filename.toLowerCase();
  for (const [ext, lang] of Object.entries(extensionToMonacoLanguage)) {
    if (lower.endsWith(`.${ext}`)) {
      return lang;
    }
  }
  return "plaintext";
}

export const isStructuredText = (filename: string): boolean => {
  const structuredTextExtensionTypes = new RegExp(
    `\\.(${structuredTextFileTypes.join("|")})$`,
    "i",
  );
  return structuredTextExtensionTypes.test(filename.toLowerCase());
};

/** Extension list for files viewable in the Document Panel. */
export const documentViewableFileTypes = [
  "html",
  "htm",
  "pdf",
  ...structuredTextFileTypes,
];

/** Glob patterns matching document-viewable files, for use with includePatterns filters. */
export const documentViewableGlobPatterns = documentViewableFileTypes.map(
  (ext) => `**/*.${ext}`,
);

/** Files viewable in the Document Panel: web-renderable (HTML/PDF) or structured text (JSON/YAML/XML). */
export const isDocumentViewable = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return documentViewableFileTypes.some((ext) => lower.endsWith(`.${ext}`));
};

export const isVisibleMedia = (filename: string) => {
  return (
    isImage(filename) ||
    isVideo(filename) ||
    isText(filename) ||
    isMarkdown(filename) ||
    isWebRenderable(filename)
  );
};

export function canHaveTopics(filename: string): boolean {
  const allRoboticsFileTypes = [
    ...roboticsFileTypes,
    ...customRoboticsFileTypes,
  ];
  const roboticFileExtensionTypes = new RegExp(
    `\\.(${allRoboticsFileTypes.join("|")})$`,
    "i",
  );
  return roboticFileExtensionTypes.test(filename.toLowerCase());
}
